import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Modal } from 'app-components';

import { ModalContextType } from '../../../contexts/modalContext';
import { useExchangeConditions } from '../../../hooks/useExchangeConditions';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import Form from '../../../lib/wrappers/Form';
import UniversalInput from '../../../lib/wrappers/UniversalInput';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import Image from '../../../components/common/image/Image';
import { ENROLLMENT_ACTIONS, FROM_TAB, PLAN_TYPE } from './modalTypes';
import { useAppDispatch, useAppSelector, useModal } from '../../../hooks';
import useCustomGrouping from '../hooks/useCustomGrouping';
import CapitalizedButton from '../components/common/CapitalizedButton';
import { getIsState } from '../../../reducers/appReducer';

interface IModalProps extends ModalContextType {
    actualDatesEndOfCurrentMonth?: string;
    actualDatesEndOfNextMonth?: string;
    actualDatesEndOfMonthAfterNext?: string;
    typeOfPlan: PLAN_TYPE;
    fromTab: FROM_TAB;
    enrollmentAction: ENROLLMENT_ACTIONS;
    disenrollGroup: {
        logoUrl: string;
        issuerName: string;
        planName: string;
    };
    dentalDetails: {
        planName: string;
    };
    healthDetails: {
        planName: string;
    };
    disenrollmentState: {
        reason: string;
        reasonOther: string;
    };
}

enum DISENROLLMENT_STEPS {
    CHOOSE_DATE = 'CHOOSE_DATE',
    CONFIRM_END_DATE = 'CONFIRM_END_DATE',
    CONFIRMATION = 'CONFIRMATION',
}

export default function DisenrollmentFlowModal(props: IModalProps) {
    const {
        actualDatesEndOfCurrentMonth,
        actualDatesEndOfNextMonth,
        actualDatesEndOfMonthAfterNext,
        typeOfPlan,
        disenrollGroup,
        dentalDetails,
        healthDetails,
        closeAllModals,
        disenrollmentState,
        enrollmentAction,
        fromTab,
    } = props;
    const { isCancelDisenrollmentButtonVisible, isUnableToEnrollAgainWarningVisible, areCancelContinueControlsVisible } = useExchangeConditions();
    const intl = useIntl();
    const { openModal } = useModal();
    const { isMemberLevelDisenrollEnabled } = useAppSelector(state => state.common.environment.flags);
    const exchangeName = useAppSelector(state => state.common.environment.exchangeName);
    const isState = useSelector(getIsState);
    const dispatch = useAppDispatch();
    const { submitDisenrollment } = useCustomGrouping({ dispatch });

    function onClose(e) {
        e?.preventDefault();
        closeAllModals();
    }

    async function onContinue() {
        try {
            const response = await submitDisenrollment({
                params: {
                    typeOfPlan,
                    healthDetails,
                    dentalDetails,
                    disenrollGroup,
                    disenrollOption: disenrollmentState,
                    isMemberLevelDisenrollEnabled,
                    disenrollDateSelected: disenrollmentData.disenrollDate,
                    disenrollDateOptionSelected: disenrollmentData?.disenrollOptionSelected,
                },
            });
            if (response.data?.message === 'success') {
                closeAllModals();
                openModal('submitDisenrollModal', { enrollmentAction, fromTab });
            } else {
                openModal('disenrollFailureModal');
            }
        } catch (e) {
            openModal('disenrollFailureModal');
        }
    }

    const [currentStep, setCurrentStep] = useState(DISENROLLMENT_STEPS.CHOOSE_DATE);
    const [disenrollmentData, setDisenrollmentData] = useState({ disenrollDate: '', disenrollOptionSelected: '' });

    const disenrollDateConfig = {
        CURRENT_MONTH: actualDatesEndOfCurrentMonth,
        NEXT_MONTH: actualDatesEndOfNextMonth,
        MONTH_AFTER_NEXT_MONTH: actualDatesEndOfMonthAfterNext,
    };

    const headerConfig = {
        [DISENROLLMENT_STEPS.CHOOSE_DATE]: 'indportal.plansummary.selectTerminationDate',
        [DISENROLLMENT_STEPS.CONFIRM_END_DATE]: 'indportal.plansummary.confirmEndDate',
        [DISENROLLMENT_STEPS.CONFIRMATION]: 'indportal.plansummary.confirmation',
    };

    const showContinueOrSubmitButton = 
        (areCancelContinueControlsVisible && FROM_TAB.PLANSUMMARY !== fromTab) || isState.MN;

    const footerConfig = {
        [DISENROLLMENT_STEPS.CHOOSE_DATE]: {
            buttonText: showContinueOrSubmitButton ? 'button.continue' : 'button.ok',
            onClick: () => setCurrentStep(DISENROLLMENT_STEPS.CONFIRM_END_DATE),
        },
        [DISENROLLMENT_STEPS.CONFIRM_END_DATE]: {
            buttonText: showContinueOrSubmitButton ? 'button.continue' : 'button.ok',
            onClick: () => setCurrentStep(DISENROLLMENT_STEPS.CONFIRMATION),
        },
        [DISENROLLMENT_STEPS.CONFIRMATION]: {
            buttonText: showContinueOrSubmitButton ? 'button.submit' : 'button.ok',
            onClick: onContinue,
        },
    };

    const disenrollDateOptions = [
        {
            value: 'CURRENT_MONTH',
            condition: actualDatesEndOfCurrentMonth,
            text: intl.formatMessage(
                { id: 'indportal.plansummary.disenrollmentDates.lastDayCurrentMonth' },
                { actualDatesEndOfCurrentMonth },
            ),
        },
        {
            value: 'NEXT_MONTH',
            condition: actualDatesEndOfNextMonth,
            text: intl.formatMessage(
                { id: 'indportal.plansummary.disenrollmentDates.lastDayNextMonth' },
                { actualDatesEndOfNextMonth },
            ),
        },
        {
            value: 'MONTH_AFTER_NEXT_MONTH',
            condition: actualDatesEndOfMonthAfterNext,
            text: intl.formatMessage(
                { id: 'indportal.plansummary.disenrollmentDates.lastDayAfterMonth' },
                { actualDatesEndOfMonthAfterNext },
            ),
        },
    ];

    return (
        <Modal id="disenrollmentFlowModal">
            <Modal.Header id="disenrollmentFlowModalHeader" titleId={headerConfig[currentStep]} onHide={onClose} />
            <Modal.Body id="disenrollmentFlowModalBody">
                <ConditionalRendering isTrue={currentStep === DISENROLLMENT_STEPS.CHOOSE_DATE}>
                    <Form formName="disenrollmentDateSelctionForm">
                        <UniversalInput
                            label={{
                                isLabelVisible: false,
                                ulClass: ['margin-b-20  margin-l-20'],
                            }}
                            inputData={{
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                liClass: ['text-left'],
                                currentData: disenrollmentData,
                                name: 'disenrollOptionSelected',
                                value: disenrollmentData.disenrollOptionSelected,
                                options: disenrollDateOptions.filter(o => !!o.condition),
                            }}
                            errorClasses={['margin-l-0']}
                            inputActions={{
                                onFieldChange: ({ disenrollOptionSelected }) =>
                                    setDisenrollmentData({
                                        disenrollOptionSelected,
                                        disenrollDate: disenrollDateConfig[disenrollOptionSelected],
                                    }),
                            }}
                        />
                    </Form>
                    <div className="margin20-t usa-alert gi-alert gi-alert__left-accent gi-alert__info margin0-b">
                        <div className="gi-alert__body">
                            <p className="gi-alert__text">
                                <FormattedMessage id="indportal.plansummary.needSpecificTermDate" values={{ exchangeName }} />
                            </p>
                        </div>
                    </div>
                </ConditionalRendering>

                <ConditionalRendering isTrue={currentStep === DISENROLLMENT_STEPS.CONFIRM_END_DATE}>
                    <p>
                        <FormattedMessage
                            id='indportal.plansummary.customGrouping.chosenEndDate.lastCoverageDate'
                            values={{
                                selectedCoverageDate: disenrollmentData.disenrollDate,
                                planName1: disenrollGroup?.planName,
                                planName2: typeOfPlan === PLAN_TYPE.BOTH ? dentalDetails?.planName : undefined,
                            }}
                        />
                    </p>
                    <div className="margin20-t margin0-b disenroll-confirm-box">
                        <Image
                            source={disenrollGroup.logoUrl ?? disenrollGroup['planLogoUrl']}
                            desc={disenrollGroup?.planName}
                            name="chip"
                            classes={['pull-left']}
                            isMessageFormattingEnabled={false}
                        />
                        <ConditionalRendering isTrue={typeOfPlan === PLAN_TYPE.BOTH}>
                            <Image
                                source={dentalDetails?.['planLogoUrl']}
                                desc={dentalDetails?.planName}
                                name="chip"
                                classes={['pull-right']}
                                isMessageFormattingEnabled={false}
                            />
                        </ConditionalRendering>
                    </div>
                </ConditionalRendering>

                <ConditionalRendering isTrue={currentStep === DISENROLLMENT_STEPS.CONFIRMATION}>
                    <div className="margin20-t usa-alert gi-alert gi-alert__left-accent gi-alert__info margin0-b disenroll-confirm-box">
                        <div className="gi-alert__body">
                            <p className="gi-alert__text">
                                <FormattedMessage
                                    id='indportal.plansummary.disenrollConfirmContent.coverageEndDate'
                                    values={{
                                        selectedCoverageDate: disenrollmentData.disenrollDate,
                                        planName1: disenrollGroup?.planName,
                                        planName2: typeOfPlan === PLAN_TYPE.BOTH ? dentalDetails?.planName : undefined,
                                    }}
                                />
                            </p>
                        </div>
                    </div>
                    <div className="margin20-t usa-alert gi-alert gi-alert__left-accent gi-alert__info margin0-b">
                        <div className="gi-alert__body">
                            <p className="gi-alert__text">
                                <FormattedMessage 
                                    id="indportal.plansummary.note"
                                    values={{
                                        strong: chunks => <strong>{chunks}</strong>
                                    }}
                                />
                            </p>
                            <ul className="margin-l-20 gi-alert__text">
                                <li>
                                    <FormattedMessage id="indportal.plansummary.disenrollConfirmPt1" />
                                </li>
                                <ConditionalRendering isTrue={isUnableToEnrollAgainWarningVisible}>
                                    <li>
                                        <FormattedMessage id="indportal.plansummary.disenrollConfirmPt2" />
                                    </li>
                                </ConditionalRendering>
                            </ul>
                        </div>
                    </div>
                </ConditionalRendering>
            </Modal.Body>
            <Modal.Controls>
                <ConditionalRendering isTrue={isCancelDisenrollmentButtonVisible && currentStep === DISENROLLMENT_STEPS.CHOOSE_DATE}>
                    <CapitalizedButton 
                        id="disenrollmentFlowModalCancelButton" 
                        onClickHandler={onClose} 
                        variant="secondary" 
                        labelId="button.cancel" 
                    />
                </ConditionalRendering>

                <ConditionalRendering isTrue={currentStep === DISENROLLMENT_STEPS.CONFIRM_END_DATE}>
                    <CapitalizedButton
                        id="disenrollmentFlowModalUpdateButton"
                        onClickHandler={() => setCurrentStep(DISENROLLMENT_STEPS.CHOOSE_DATE)}
                        variant="secondary"
                        labelId="indportal.plansummary.updateTermDate"
                        leftMargin={true}
                    />
                </ConditionalRendering>

                <CapitalizedButton
                    id="disenrollmentFlowModalTextButton"
                    onClickHandler={footerConfig[currentStep].onClick}
                    variant="primary"
                    labelId={footerConfig[currentStep].buttonText}
                    isDisabled={!disenrollmentData.disenrollOptionSelected}
                    leftMargin={true}
                />
            </Modal.Controls>
        </Modal>
    );
}
